import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { TrendingUp, Download, Trophy, Users2, IndianRupee, Percent, ArrowUpDown, Loader2, Sparkles } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { minDelay } from "@/lib/ux";
import { Loading, ErrorView, EmptyState } from "@/components/StateViews";
import { useAuth } from "@/contexts/AuthContext";
import { fmtINR } from "@/lib/format";
import DateRangePicker from "@/components/DateRangePicker";
import { toast } from "sonner";

const DIMENSIONS = [
  ["campaign", "Campaign"], ["ad", "Ad"], ["platform", "Platform"],
  ["form", "Form"], ["segment", "Business Segment"],
];

const COLUMNS = [
  { key: "name", label: "Source", align: "left" },
  { key: "leads", label: "Leads" },
  { key: "ace_king_pct", label: "Ace+King %", suffix: "%" },
  { key: "won", label: "Won" },
  { key: "conversion", label: "Conversion %", suffix: "%", accent: true },
  { key: "lost", label: "Lost" },
  { key: "junk", label: "Junk" },
  { key: "pipeline_value", label: "Pipeline ₹", currency: true },
  { key: "sales", label: "Sales ₹", currency: true },
  { key: "avg_deal", label: "Avg Deal ₹", currency: true },
];

// dimension -> Leads page query param for deep-linking
const DEEP_LINK = { campaign: "campaign_name", platform: "platform", segment: "business_segment" };

export default function Performance() {
  const { user, options } = useAuth();
  const navigate = useNavigate();
  const [dimension, setDimension] = useState("campaign");
  const [filters, setFilters] = useState({ created_from: "", created_to: "", service_interested: "" });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [sort, setSort] = useState({ key: "leads", dir: "desc" });
  const [exporting, setExporting] = useState(false);
  const [tidying, setTidying] = useState(false);

  const isAdmin = user?.role === "Admin";

  const tidySources = async () => {
    setTidying(true);
    try {
      const { data } = await api.post("/imports/normalize-sources");
      const d = data.data;
      toast.success(`Tidied sources · ${d.platform_fixed} platform + ${d.names_fixed} name fixes across ${d.leads_scanned} leads`);
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setTidying(false); }
  };

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const params = { dimension, ...Object.fromEntries(Object.entries(filters).filter(([, v]) => v)) };
      const { data } = await minDelay(api.get("/performance", { params }));
      setData(data.data);
    } catch (e) { setError(apiError(e)); }
    finally { setLoading(false); }
  }, [dimension, filters]);

  useEffect(() => { load(); }, [load]);

  const toggleSort = (key) => setSort((s) => ({ key, dir: s.key === key && s.dir === "desc" ? "asc" : "desc" }));

  const sortedRows = data?.rows ? [...data.rows].sort((a, b) => {
    const x = a[sort.key], y = b[sort.key];
    const cmp = sort.key === "name" ? String(x).localeCompare(String(y)) : ((Number(x) || 0) - (Number(y) || 0));
    return sort.dir === "desc" ? -cmp : cmp;
  }) : [];

  const deepLink = (name) => {
    if (name === "(none)") return;
    const param = DEEP_LINK[dimension];
    if (param) navigate(`/leads?${param}=${encodeURIComponent(name)}`);
    else navigate(`/leads?search=${encodeURIComponent(name)}`); // ad / form
  };

  const doExport = async () => {
    setExporting(true);
    try {
      const params = { dimension, ...Object.fromEntries(Object.entries(filters).filter(([, v]) => v)) };
      const res = await api.get("/performance/export", { params, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = `performance_${dimension}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Performance exported");
    } catch (e) { toast.error(apiError(e)); }
    finally { setExporting(false); }
  };

  if (loading && !data) return <Loading label="Loading performance..." />;
  if (error) return <ErrorView message={error} onRetry={load} />;

  const s = data.summary;
  const tiles = [
    { label: "Total Leads", value: s.total_leads, icon: Users2, tone: "text-loop", bg: "bg-sky/50" },
    { label: "Overall Conversion", value: `${s.conversion}%`, icon: Percent, tone: "text-emerald-600", bg: "bg-emerald-50" },
    { label: "Total Sales", value: fmtINR(s.total_sales), icon: IndianRupee, tone: "text-navy", bg: "bg-mist" },
    { label: "Best Converting", value: s.best_source ? `${s.best_source.name}` : "—", sub: s.best_source ? `${s.best_source.conversion}%` : "", icon: Trophy, tone: "text-amber-500", bg: "bg-amber-50" },
  ];

  return (
    <div className="space-y-6" data-testid="performance-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 font-sora text-3xl font-bold text-navy"><TrendingUp className="h-7 w-7 text-loop" />Meta Performance</h1>
          <p className="flex items-center gap-2 text-sm text-ink/60">
            Which sources actually convert — leads, quality & ROI by source.
            {loading && data && <span data-testid="perf-refreshing" className="flex items-center gap-1 text-loop"><Loader2 className="h-3.5 w-3.5 animate-spin" />Refreshing…</span>}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <DateRangePicker
            from={filters.created_from}
            to={filters.created_to}
            onChange={({ created_from, created_to }) => setFilters((f) => ({ ...f, created_from, created_to }))}
            testid="perf-date-range"
          />
          <select value={filters.service_interested} onChange={(e) => setFilters((f) => ({ ...f, service_interested: e.target.value }))} data-testid="perf-service-filter" className="h-9 rounded-lg border border-sky px-2 text-xs outline-none focus:border-loop">
            <option value="">All services</option>
            {options?.services?.map((sv) => <option key={sv} value={sv}>{sv}</option>)}
          </select>
          {isAdmin && (
            <button onClick={tidySources} disabled={tidying} data-testid="perf-tidy-btn" className="flex h-9 items-center gap-1.5 rounded-lg border border-sky px-3 text-sm font-medium text-navy hover:border-loop hover:text-loop disabled:opacity-60" title="Normalize platform casing & trim campaign/ad names so ROI doesn't split across duplicates">
              {tidying ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}Tidy sources
            </button>
          )}
          {isAdmin && (
            <button onClick={doExport} disabled={exporting} data-testid="perf-export-btn" className="flex h-9 items-center gap-1.5 rounded-lg border border-sky px-3 text-sm font-medium text-navy hover:border-loop hover:text-loop disabled:opacity-60">
              {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}Export
            </button>
          )}
        </div>
      </div>

      {/* Group-by tabs */}
      <div className="flex flex-wrap gap-1 rounded-xl border border-sky/70 bg-white p-1" data-testid="perf-dimension-tabs">
        {DIMENSIONS.map(([key, label]) => (
          <button key={key} data-testid={`perf-dim-${key}`} onClick={() => setDimension(key)} className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${dimension === key ? "bg-loop text-white" : "text-ink/60 hover:bg-mist"}`}>{label}</button>
        ))}
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {tiles.map((t) => (
          <div key={t.label} data-testid={`perf-tile-${t.label.toLowerCase().replace(/\s+/g, "-")}`} className="rounded-2xl border border-sky/70 bg-white p-4">
            <div className={`mb-3 flex h-9 w-9 items-center justify-center rounded-xl ${t.bg} ${t.tone}`}><t.icon className="h-[18px] w-[18px]" /></div>
            <p className="truncate font-sora text-xl font-bold text-navy">{t.value}</p>
            <p className="text-xs text-ink/60">{t.label}{t.sub ? ` · ${t.sub}` : ""}</p>
          </div>
        ))}
      </div>

      {/* Table */}
      {!sortedRows.length ? (
        <EmptyState title="No data for this range" subtitle="Try a wider date range or a different grouping." />
      ) : (
        <div className="overflow-hidden rounded-2xl border border-sky/70 bg-white">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-mist text-left text-xs font-semibold uppercase tracking-wide text-ink/50">
                <tr>
                  {COLUMNS.map((col) => (
                    <th key={col.key} onClick={() => toggleSort(col.key)} data-testid={`perf-sort-${col.key}`} className={`cursor-pointer whitespace-nowrap px-4 py-3 hover:text-loop ${col.align === "left" ? "text-left" : "text-right"}`}>
                      <span className={`inline-flex items-center gap-1 ${col.align === "left" ? "" : "justify-end"}`}>
                        {col.label}<ArrowUpDown className={`h-3 w-3 ${sort.key === col.key ? "text-loop" : "text-ink/30"}`} />
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-sky/50">
                {sortedRows.map((r, idx) => (
                  <tr key={`${r.name}-${idx}`} data-testid={`perf-row-${r.name}`} onClick={() => deepLink(r.name)} className="cursor-pointer transition-colors hover:bg-sky/20">
                    {COLUMNS.map((col) => (
                      <td key={col.key} className={`whitespace-nowrap px-4 py-3 ${col.align === "left" ? "font-medium text-navy" : "text-right text-ink/80"} ${col.accent ? "font-semibold text-emerald-600" : ""}`}>
                        {col.currency ? fmtINR(r[col.key]) : `${r[col.key]}${col.suffix || ""}`}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
